import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActionCard from '../cards/ActionCard';

/**
 * WE-page "Action Library" shelf — section header + horizontal-scroll
 * carousel of ActionCard. The All/My tabs, search bar, and scope
 * filter chips that used to live here have moved to the dedicated
 * `/all-actions` page (opened via the "View all actions →" link).
 */
const MADSection = ({ allChallenges, myChallenges, joinedActions, onJoinChallenge, currentUserId, isHRAdmin }) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);

  const myJoinedChallengeIds = useMemo(() => new Set(myChallenges.map(c => c.id)), [myChallenges]);

  const isJoined = (challenge) => {
    if (!challenge) return false;
    if (joinedActions.has(challenge.id)) return true;
    if (myJoinedChallengeIds.has(challenge.id)) return true;
    if (!currentUserId) return false;
    const participants = challenge.participants || [];
    return participants.some(p => (typeof p === 'string' ? p : p?.user_id) === currentUserId);
  };
  const isExpired = (challenge) => {
    if (!challenge?.end_date) return false;
    return new Date(challenge.end_date) < new Date();
  };

  return (
    <div data-testid="all-mad-section" style={{ marginTop: -8, background: '#EDF7F4', borderRadius: 16, padding: '16px 12px' }}>
      {/* Section header — title left, "View all actions →" right.
          Layout mirrors the community "Featured Events / View all
          events →" header (text + arrow only — no card style copied). */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1F2937', margin: 0 }}>Action Library</h3>
        <button data-testid="view-all-actions-link" type="button" onClick={() => navigate('/all-actions')}
          style={{ background: 'none', border: 'none', fontSize: 12, fontWeight: 600, color: '#52B8AC', cursor: 'pointer', padding: 0 }}>
          View all actions &rarr;
        </button>
      </div>

      {allChallenges.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 20px', background: '#FFFFFF', borderRadius: 12, border: '1px solid #E5E7EB', color: '#6B7280', fontSize: 13 }}>
          No actions available yet.
        </div>
      ) : (
        <>
          {/* Single-card-at-a-time carousel. Each slide is 100%-wide and
              scroll-snap-aligned, so the browser snaps to exactly one
              card per swipe — no peek of the next card. Dot pagination
              is driven by scrollLeft / clientWidth (same pattern as
              the community `Featured Events` carousel). */}
          <div data-testid="mad-carousel"
            style={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch', gap: 0 }}
            className="hide-scrollbar"
            onScroll={(e) => {
              const w = e.currentTarget.clientWidth;
              if (w > 0) setActiveIndex(Math.round(e.currentTarget.scrollLeft / w));
            }}>
            {allChallenges.map(challenge => (
              <div key={challenge.id}
                style={{ flexShrink: 0, width: '100%', scrollSnapAlign: 'start', boxSizing: 'border-box' }}>
                <ActionCard
                  challenge={challenge}
                  isJoined={isJoined(challenge)}
                  isExpired={isExpired(challenge)}
                  onJoin={onJoinChallenge}
                  onOpen={() => navigate(`/challenges/${challenge.id}`)}
                  isHRAdmin={isHRAdmin}
                />
              </div>
            ))}
          </div>
          {/* Dot pagination — one dot per action, active dot elongated. */}
          {allChallenges.length > 1 && (
            <div data-testid="mad-carousel-dots"
              style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 10 }}>
              {allChallenges.map((_, i) => (
                <span key={i}
                  style={{ width: i === activeIndex ? 18 : 6, height: 6, borderRadius: 3,
                    background: i === activeIndex ? '#52B8AC' : '#D1D5DB',
                    transition: 'width 0.2s ease' }} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MADSection;
